import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Menu, X } from 'lucide-react';
import './ClubLoginPage.css';
import ClubLogin from './ClubLogin';

// Assets
import darkLogo from '../../Assets/Images/Dark_Logo_Transparent.png';

// SEO structured data
const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PrivateIQ',
  url: 'https://privateiq.golf/',
  logo: 'https://privateiq.golf/logo192.png',
};

const ClubLoginPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll handler for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO
  const seoTitle = 'Club Login — PrivateIQ';
  const seoDescription =
    'Log in to your PrivateIQ club dashboard. Access qualified golfer prospects, manage outreach, and grow your membership.';

  return (
    <div className="club-login-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href="https://privateiq.golf/myclub/login" />
        <meta name="theme-color" content="#2c5c4f" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="PrivateIQ" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content="https://privateiq.golf/myclub/login" />
        <meta property="og:image" content="https://privateiq.golf/og-cover.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>

      {/* ===== HEADER ===== */}
      <header className={`cl-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="cl-header-inner">
          {/* Logo */}
          <Link to="/" className="cl-header-logo">
            <img src={darkLogo} alt="PrivateIQ" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="cl-header-nav">
            <Link to="/">For Golfers</Link>
            <Link to="/myclub/login" className="cl-nav-active">Club Login</Link>
          </nav>

          {/* Auth Buttons (Desktop) */}
          <div className="cl-header-auth">
            <div className="cl-header-divider"></div>
            <Link to="/myclub/login" className="cl-header-login">Club Login</Link>
            <Link to="/myclub/register" className="cl-header-register">
              Get Started
            </Link>
          </div>

          {/* Mobile */}
          <div className="cl-header-mobile">
            <Link to="/myclub/register" className="cl-mobile-register">
              Get Started
            </Link>
            <button
              className="cl-header-mobile-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="cl-w-5 cl-h-5" /> : <Menu className="cl-w-5 cl-h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="cl-mobile-menu">
            <nav className="cl-mobile-nav">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>For Golfers</Link>
              <Link to="/myclub/login" onClick={() => setMobileMenuOpen(false)}>Club Login</Link>
            </nav>
            <div className="cl-mobile-auth">
              <Link
                to="/myclub/register"
                className="cl-mobile-register-btn"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ===== LOGIN SECTION ===== */}
      <section className="cl-login-section">
        <div className="cl-login-bg"></div>
        <div className="cl-login-card">
          <div className="cl-login-badge">For Private Clubs</div>
          <h1 className="cl-login-title">Club Login</h1>
          <p className="cl-login-subtitle">
            Access your dashboard to connect with qualified prospects.
          </p>
          <ClubLogin />
          <div className="cl-login-links">
            <p>
              Don't have an account?{' '}
              <Link to="/myclub/register">Register your club</Link>
            </p>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="cl-footer">
        <div className="cl-section-container">
          <div className="cl-footer-grid">
            <div className="cl-footer-brand">
              <div className="cl-footer-logo">privateIQ</div>
              <p className="cl-footer-tagline">
                The intelligent way for clubs to discover qualified members.
              </p>
            </div>
            <div className="cl-footer-links">
              <h4>Platform</h4>
              <ul>
                <li><Link to="/">For Golfers</Link></li>
                <li><Link to="/myclub/register">For Clubs</Link></li>
              </ul>
            </div>
            <div className="cl-footer-links">
              <h4>Company</h4>
              <ul>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>
            <div className="cl-footer-links">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/termsofservice">Terms of Service</Link></li>
                <li><Link to="/privacypolicy">Privacy Policy</Link></li>
                <li><Link to="/privacy-choices">Privacy Choices</Link></li>
              </ul>
            </div>
          </div>
          <div className="cl-footer-bottom">
            <p>&copy; 2025 Non-Target LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClubLoginPage;
